/**
 * ToastMessage — 한 줄짜리 알림.
 *
 * ── 2026-08-27 검수 ────────────────────────────────────────────────
 * 이 파일은 Flutter(toast_message.dart)에서 옮겨 온 그대로였다. 세 가지가
 * peeka 규칙 밖이었다:
 *
 * ① **오른쪽 ✕ 가 늘 있었다.** onClose 가 없어도 장식으로 그려서, 왼쪽 상태
 *    아이콘과 오른쪽 ✕ 가 한 줄에 나란히 뜨는 '아이콘 두 개' 가 됐다. 게다가
 *    눌러도 아무 일이 없는 ✕ 다 — 닫히는 줄 알고 누르면 아무 반응이 없다.
 *    이제 **닫을 수 있을 때만** 그린다.
 * ② 아이콘이 글리프(ℹ ✓ ⚠ ✗)였다. 서체에 없는 글자라 기기마다 다르게 그려지고,
 *    브랜드 규칙(BRAND §3.6 이모지 금지)과도 어긋난다 → peeka 아이콘으로.
 * ③ 색이 템플릿 토큰(gray-100 · fun-blue-10 · shiro-white …)이었다 →
 *    의미 토큰(--bg-success 등)으로.
 *
 * ⚠ 성공만 Check, 나머지는 Info 다. 종류를 나르는 것은 **색과 글**이고,
 *   아이콘은 '알림이 떴다'는 표시다 — 아이콘까지 넷으로 갈리면 한 줄짜리
 *   알림에 읽을 것이 셋이 된다.
 */
import { useTranslation } from 'react-i18next';
import Icon, { type IconName } from '../primitives/Icon';
import styles from './ToastMessage.module.css';

export type ToastPurpose = 'info' | 'success' | 'warning' | 'error';

interface ToastMessageProps {
  purpose: ToastPurpose;
  text?: string;
  onClose?: () => void;
}

const purposeIcon: Record<ToastPurpose, IconName> = {
  info: 'Info',
  success: 'Check',
  warning: 'Info',
  error: 'Info',
};

function ToastMessage({ purpose, text, onClose }: ToastMessageProps) {
  const { t } = useTranslation();
  return (
    <div
      className={`${styles.toast} ${styles[purpose]}`}
      role="status"
      aria-live="polite"
    >
      <span className={styles.leadingIcon}>
        <Icon name={purposeIcon[purpose]} size="sm" />
      </span>
      <span className={styles.text}>{text}</span>
      {/* 닫기는 **있을 때만**. 위 ① 참조 */}
      {onClose && (
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label={t('action.close')}
        >
          <Icon name="Close" size="sm" />
        </button>
      )}
    </div>
  );
}

export default ToastMessage;
